import React from "react"
import { Card, CardBody, Badge } from "reactstrap"
import SectionHeader from "components/Common/SectionHeader"
import ThemePlaceholder from "components/Common/ThemePlaceholder"

// Helper function to check if value exists
const hasValue = value => {
  if (value === null || value === undefined) return false
  if (typeof value === "string" && value.trim() === "") return false
  return true
}

// Component for single address view (simpler UI)
const SingleAddressView = ({ address }) => {
  const hasAddress = hasValue(address.address)

  const addressItems = [
    { label: "Address", value: address.address, hasValue: hasAddress },
  ]

  return (
    <div className="specs-list">
      {addressItems.map((item, idx) => (
        <div
          key={idx}
          className="d-flex justify-content-between py-2 border-bottom"
        >
          <span className="text-muted">{item.label}:</span>
          <span className="fw-medium text-end">
            {item.hasValue ? (
              item.value
            ) : (
              <Badge color="light" className="text-muted">
                Not Provided
              </Badge>
            )}
          </span>
        </div>
      ))}
    </div>
  )
}

// Component for multiple addresses view (with index numbers)
const MultipleAddressView = ({ addresses }) => {
  const AddressItem = ({ address, index }) => {
    const hasShowroomName = hasValue(address.showroom_name)
    const hasShowroomAddress = hasValue(address.showroom_address)
    const hasAddress = hasValue(address.address)
    const hasEmail = hasValue(address.email)
    const hasMobile = hasValue(address.mobile)

    let addressItems = [
      {
        label: "Branch Name",
        value: address.showroom_name,
        hasValue: hasShowroomName,
      },
      {
        label: "Address",
        value: address.showroom_address || address.address,
        hasValue: hasShowroomAddress || hasAddress,
      },
      { label: "Email", value: address.email, hasValue: hasEmail },
      { label: "Mobile", value: address.mobile, hasValue: hasMobile },
    ]

    return (
      <div className="mb-3 pb-3 border-bottom">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">
            {hasShowroomName ? address.showroom_name : `Address ${index + 1}`}
          </h6>
          <Badge color="light">#{index + 1}</Badge>
        </div>

        <div className="specs-list">
          {addressItems.map((item, idx) => (
            <div key={idx} className="d-flex justify-content-between py-1">
              <span className="text-muted">{item.label}:</span>
              <span className="fw-medium text-end">
                {item.hasValue ? (
                  item.value
                ) : (
                  <Badge color="light" className="text-muted">
                    Not Provided
                  </Badge>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="specs-list">
      {addresses.map((address, index) => (
        <AddressItem
          key={address.id || index}
          address={address}
          index={index}
        />
      ))}
    </div>
  )
}

const AddressCard = ({ addresses, loading, title = "Address", action = null }) => {
  if (loading) {
    return (
      <Card className="border-0 shadow-sm mb-4">
        <CardBody className="p-3">
          <SectionHeader icon="bx bx-map" title={title} action={action} />
          <div className="py-2">
            <ThemePlaceholder
              style={{ width: "100%", height: 150, borderRadius: 4 }}
            />
          </div>
        </CardBody>
      </Card>
    )
  }

  const hasAddresses =
    addresses && Array.isArray(addresses) && addresses.length > 0
  const isSingleAddress = hasAddresses && addresses.length === 1
  const isMultipleAddresses = hasAddresses && addresses.length > 1

  return (
    <Card className="border-0 shadow-sm mb-4">
      <CardBody className="p-3">
        <SectionHeader
          icon="bx bx-map"
          title={
            isMultipleAddresses
              ? `${
                  isSingleAddress
                    ? title
                    : isMultipleAddresses
                    ? "Addresses"
                    : ""
                } (${addresses.length})`
              : title
          }
          action={action}
        />

        {isSingleAddress ? (
          <SingleAddressView address={addresses[0]} />
        ) : isMultipleAddresses ? (
          <MultipleAddressView addresses={addresses} />
        ) : (
          <div className="text-center py-3">
            <i className="bx bx-map display-4 text-muted mb-2"></i>
            <p className="text-muted mb-0">No addresses found</p>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export default AddressCard
